/**
 * Utility functions for SmartChoiceDemo
 */
import { PortalIdentifier, PortalName } from 'parcels/src/common/Portal';
import { mockBehavioralGuestPortalMemberInfo } from 'parcels/src/common/Portal/mockBehavioralGuestPortalMemberInfo';
import { mockPortalMemberInfo } from 'parcels/src/common/Portal/mockPortalMemberInfo';

import { createThemeOverride, portalNameThemeMap } from '../theme/theme';
import { DemoSettings, STORAGE_KEY, defaultSettings } from './constants';

/**
 * Load saved settings from localStorage
 * Returns default settings if no saved settings exist or on error
 */
export const loadSavedSettings = (): DemoSettings => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return { ...defaultSettings, ...JSON.parse(saved) };
    }
  } catch {
    // Silently fail if localStorage is unavailable
  }
  return defaultSettings;
};

/**
 * Save settings to localStorage
 * Returns the timestamp if successful, null otherwise
 */
export const saveSettings = (settings: DemoSettings): string | null => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    return new Date().toLocaleTimeString();
  } catch {
    // Silently fail if localStorage is unavailable
    return null;
  }
};

/**
 * Clear saved settings from localStorage
 */
export const clearSavedSettings = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Silently fail if localStorage is unavailable
  }
};

/**
 * Map portal selection to proper PortalName enum value
 */
const getPortalNameValue = (portalName: string): string => {
  switch (portalName) {
    case 'laww':
      return PortalName.LAWW; // 'LAWW' - uppercase required
    case 'umr':
      return PortalName.UMR; // 'umr'
    case 'geha':
      return 'geha'; // GEHA doesn't have a PortalName enum, uses portalIdentifier
    case 'uhc':
    default:
      return PortalName.UHC; // 'uhc'
  }
};

/**
 * Build portal data object based on current portal configuration
 * @param portalName - The portal name (uhc, laww, umr, geha)
 * @param basePath - The base URL path
 * @param theme - The theme (uhc, optum)
 * @param language - The language code
 * @param useFallbackLob - If true, use 'eni' LOB for Lagoon instead of portal-specific LOB (useful when portal LOB data is unavailable)
 */
export const buildPortalData = (
  portalName: string,
  basePath: string,
  theme: string,
  language: string,
  useFallbackLob: boolean = false
) => {
  let themeOverride = {};
  let portalIdentifier = PortalIdentifier.STANDARD;

  // Always use the correct portal name - don't change it based on fallback
  const resolvedPortalName = getPortalNameValue(portalName);

  // Apply theme overrides based on portal name
  if (portalName === 'umr') {
    themeOverride = createThemeOverride(portalNameThemeMap[PortalName.UMR]);
  } else if (portalName === 'geha') {
    themeOverride = createThemeOverride(
      portalNameThemeMap[PortalIdentifier.GEHA_V2]
    );
    portalIdentifier = PortalIdentifier.GEHA_V2;
  }
  // Note: LAWW/Optum theme is handled by the theme property, not themeOverride

  // Select appropriate member info based on portal
  // LAWW uses behavioral guest member info, others use standard mock member info
  const portalMemberInfo =
    portalName === 'laww'
      ? mockBehavioralGuestPortalMemberInfo
      : mockPortalMemberInfo;

  // Base portal config that applies to all portals
  const basePortalConfig = {
    basePath: basePath.replace(/^\//, ''), // Remove leading slash for consistency
    theme,
    themeOverride:
      Object.keys(themeOverride).length > 0 ? themeOverride : undefined,
    language,
    homePage: '/',
    includeBaseCss: true,
    isGuestUser: false,
    headerChipsEnabled: false,
    // Use lobOverride to force 'eni' LOB when fallback is enabled
    // This allows Lagoon to load 'eni' data while keeping LAWW portal identity
    ...(useFallbackLob && { lobOverride: 'eni' }),
  };

  // LAWW-specific configuration (based on storybook setup)
  if (portalName === 'laww') {
    return {
      portalName: resolvedPortalName,
      portalIdentifier,
      portalConfig: {
        ...basePortalConfig,
        userTier: '2',
        isObh: true,
        showEapFilter: true,
        lockEapFilter: false,
        preSelectEapFilter: false,
        showVirtualVisits: true,
        showOnlineScheduling: true,
        stickyHeaderDisabled: true,
        isEAPEWSClient: true,
        isSupportFinder: false,
      },
      portalMemberInfo,
      portalUrls: [],
    };
  }

  // Default configuration for other portals
  return {
    portalName: resolvedPortalName,
    portalIdentifier,
    portalConfig: basePortalConfig,
    portalMemberInfo,
    portalUrls: [],
  };
};

/**
 * Get theme and basePath defaults based on portal selection
 */
export const getPortalDefaults = (
  portalName: string
): { theme: string; basePath: string; useFallbackLob: boolean } => {
  switch (portalName) {
    case 'laww':
      // LAWW uses Optum theme and requires fallback LOB in dev environments
      // because OBH Lagoon data typically doesn't exist
      return { theme: 'optum', basePath: '/laww', useFallbackLob: true };
    case 'umr':
      return { theme: 'uhc', basePath: '/umr', useFallbackLob: false };
    case 'geha':
      return { theme: 'uhc', basePath: '/geha', useFallbackLob: false };
    default:
      return { theme: 'uhc', basePath: '/connect', useFallbackLob: false };
  }
};
